import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Paper,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Business, Star, TrendingUp } from '@mui/icons-material';
import { useLocale } from '../contexts/LocaleContext';
import { trackHomepageView, trackCTAClick, trackAssessmentStart } from '../utils/analytics';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLocale();

  // Track homepage view
  useEffect(() => {
    trackHomepageView(language);
  }, [language]);

  const handleAssessmentClick = () => {
    trackCTAClick('hero_button', language);
    trackAssessmentStart('360', language);
    navigate(`/assessment/360`);
  };


  return (
    <Container maxWidth="lg">
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{ mb: 3, fontWeight: 'bold', lineHeight: 1.2 }}
        >
          {t('homepage.title')}
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
          {t('homepage.subtitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: '800px', mx: 'auto' }}>
          {t('homepage.description')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: '800px', mx: 'auto' }}>
          {t('homepage.intro')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: '800px', mx: 'auto' }}>
          {t('homepage.analysis')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: '800px', mx: 'auto' }}>
          {t('homepage.proof')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
          {t('homepage.invitation')}
        </Typography>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ mb: 6 }}>
        <Paper elevation={3} sx={{ p: 4, mb: 3, backgroundColor: '#f8f9fa' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Star sx={{ color: 'secondary.main', mr: 1, mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                {t('homepage.testimonial1.name')}
              </Typography>
              <Typography variant="body1" sx={{ fontStyle: 'italic', mt: 1 }}>
                {t('homepage.testimonial1.quote')}
                <br />
                {t('homepage.testimonial1.quote2')}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={3} sx={{ p: 4, backgroundColor: '#f8f9fa' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Star sx={{ color: 'secondary.main', mr: 1, mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                {t('homepage.testimonial2.name')}
              </Typography>
              <Typography variant="body1" sx={{ fontStyle: 'italic', mt: 1 }}>
                {t('homepage.testimonial2.quote')}
                <br />
                {t('homepage.testimonial2.quote2')}
                <br />
                {t('homepage.testimonial2.quote3')}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Benefits Section */}
      <Card elevation={4} sx={{ mb: 6, overflow: 'visible' }}>
        <CardContent sx={{ p: 5, textAlign: 'center' }}>
          <Business
            sx={{ fontSize: 72, color: 'secondary.main', mb: 3 }}
          />

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('homepage.comment')}
          </Typography>

          <Divider sx={{ my: 3, maxWidth: '200px', mx: 'auto' }} />

          <Box sx={{ maxWidth: '600px', mx: 'auto', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'flex-start' }}>
              <TrendingUp sx={{ color: 'secondary.main', mr: 2 }} />
              <Typography variant="h6">
                {t('homepage.benefit1')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'flex-start' }}>
              <TrendingUp sx={{ color: 'secondary.main', mr: 2 }} />
              <Typography variant="h6">
                {t('homepage.benefit2')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'flex-start' }}>
              <TrendingUp sx={{ color: 'secondary.main', mr: 2 }} />
              <Typography variant="h6">
                {t('homepage.benefit3')}
              </Typography>
            </Box>
          </Box>

          <Typography variant="h5" sx={{ mb: 4, color: 'secondary.main', fontWeight: 'bold' }}>
            {t('homepage.cta')}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={handleAssessmentClick}
            sx={{
              minWidth: 250,
              py: 2,
              px: 4,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: 'white',
              borderRadius: 3,
              boxShadow: 4,
              '&:hover': {
                boxShadow: 8,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {t('homepage.button')}
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default HomePage;